import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { isOwner, lastSeen } from "./Account";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { pct } from "../lib/format";
import { PageHeader, Panel, Empty } from "../components/Widgets";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { MessageManager } from "../components/Messages";

const BLANK = { name: "", email: "", passcode: "", agent_code: "", split_pct: 50, fee_per_merchant: 89, bonus_split_pct: "", bonus_min_new: "" };

export default function Agents() {
  const { user } = useAuth();
  const [agents, setAgents] = useState(null);
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/agents").then((r) => setAgents(r.data));
  useEffect(() => { load(); }, []);

  const remove = async (a) => {
    if (!window.confirm(`Delete ${a.name}? Their rows will become unassigned.`)) return;
    await api.delete(`/agents/${a.id}`); toast.success("Agent deleted"); load();
  };

  const list = (agents || []).filter((a) => `${a.name} ${a.email} ${a.agent_code}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <PageHeader eyebrow="Roster" title="Agents">
        <div className="relative"><Search size={14} className="absolute left-3 top-3 text-slate-500" />
          <Input data-testid="agent-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search" className="pl-8 w-56 bg-[#131B2E]" /></div>
        <Button data-testid="create-agent-button" onClick={() => setEdit(BLANK)} className="bg-blue-500 hover:bg-blue-600 text-white"><Plus size={14} className="mr-2" />Add agent</Button>
      </PageHeader>
      <Panel>
        {!agents ? <Empty>Loading…</Empty> : list.length === 0 ? <Empty>No agents yet. Add one — the agent code must match the agent column in your sheets.</Empty> : (
          <table className="tbl w-full" data-testid="agents-table">
            <thead><tr><th>Name</th><th>Login email</th><th>Agent code</th><th className="!text-right">Split</th><th className="!text-right">Fee / merchant</th><th>Bonus tier</th><th>Last sign-in</th><th></th></tr></thead>
            <tbody>{list.map((a) => (
              <tr key={a.id} data-testid={`agent-item-${a.agent_code}`}>
                <td><Link to={`/admin/agents/${a.id}`} className="hover:text-blue-300" data-testid={`agent-link-${a.agent_code}`}>{a.name}</Link></td>
                <td className="text-slate-400 font-mono text-xs">{a.email || <span className="text-slate-600">no login</span>}</td>
                <td><span className="bg-slate-800 text-blue-400 border border-blue-500/30 font-mono text-xs px-2 py-0.5 rounded">{a.agent_code}</span></td>
                <td className="text-right font-mono text-emerald-400">{pct(a.split_pct)}</td>
                <td className="text-right font-mono text-slate-300">${a.fee_per_merchant ?? 0}</td>
                <td className="font-mono text-xs text-amber-300">{a.bonus_split_pct != null && a.bonus_min_new ? `${pct(a.bonus_split_pct)} at ${a.bonus_min_new}+ new/mo` : <span className="text-slate-600">—</span>}</td>
                <td className="font-mono text-[11px] text-slate-400" data-testid={`last-login-${a.agent_code}`}>{a.has_login ? lastSeen(a.last_login_at) : "—"}</td>
                <td className="text-right space-x-3 whitespace-nowrap">
                  <button data-testid={`edit-agent-${a.agent_code}`} onClick={() => setEdit({ ...a, email: a.email || "", passcode: "", bonus_split_pct: a.bonus_split_pct ?? "", bonus_min_new: a.bonus_min_new ?? "" })} className="text-slate-500 hover:text-blue-300"><Pencil size={15} /></button>
                  {isOwner(user) && <button data-testid={`delete-agent-${a.agent_code}`} onClick={() => remove(a)} className="text-slate-500 hover:text-rose-400"><Trash2 size={15} /></button>}
                </td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </Panel>
      <HouseCodes />
      <MessageManager title="Message all agents" />
      {edit && <AgentDialog agent={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}

function AgentDialog({ agent, onClose, onSaved }) {
  const [f, setF] = useState(agent);
  const [busy, setBusy] = useState(false);
  const isNew = !agent.id;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    const num = (v) => (v === "" || v == null ? null : Number(v));
    const body = { name: f.name, email: f.email || null, agent_code: f.agent_code, split_pct: Number(f.split_pct), passcode: f.passcode || null,
      fee_per_merchant: Number(f.fee_per_merchant || 0), bonus_split_pct: num(f.bonus_split_pct), bonus_min_new: num(f.bonus_min_new) };
    try {
      isNew ? await api.post("/agents", body) : await api.put(`/agents/${agent.id}`, body);
      toast.success(isNew ? "Agent created" : "Agent updated"); onSaved();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const field = (label, k, props = {}) => (
    <div><div className="text-xs text-slate-400 mb-1.5">{label}</div>
      <Input data-testid={`agent-${k.replace(/_/g, "-")}-input`} value={f[k]} onChange={set(k)} className="bg-[#0B0F17]" {...props} /></div>
  );

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#131B2E] border-slate-800" data-testid="agent-dialog">
        <DialogHeader><DialogTitle>{isNew ? "Add agent" : `Edit ${agent.name}`}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          {field("Full name", "name", { required: true })}
          {field("Email (login — optional)", "email", { type: "email" })}
          {field(isNew || !agent.has_login ? "Passcode (needed if email set)" : "New passcode (leave blank to keep)", "passcode", { required: isNew && !!f.email, minLength: 6 })}
          <div className="grid grid-cols-3 gap-4">
            {field("Agent code (col M)", "agent_code", { required: true, className: "bg-[#0B0F17] font-mono" })}
            <div><div className="text-xs text-slate-400 mb-1.5">Split %</div>
              <Input data-testid="agent-split-input" type="number" step="0.01" min={0} max={100} required value={f.split_pct} onChange={set("split_pct")} className="bg-[#0B0F17] font-mono" /></div>
            {field("Fee / merchant $", "fee_per_merchant", { type: "number", step: "0.01", min: 0, className: "bg-[#0B0F17] font-mono" })}
          </div>
          <div className="rounded-md border border-slate-800 p-4">
            <div className="text-xs text-slate-300 mb-3">Bonus tier (optional) — higher split when enough new accounts open in the month</div>
            <div className="grid grid-cols-2 gap-4">
              {field("Bonus split %", "bonus_split_pct", { type: "number", step: "0.01", min: 0, max: 100, className: "bg-[#0B0F17] font-mono" })}
              {field("New accounts needed", "bonus_min_new", { type: "number", min: 1, className: "bg-[#0B0F17] font-mono" })}
            </div>
          </div>
          <p className="text-xs text-slate-500">Payout per merchant = (residual − fee, never below $0) × split. Negative residuals are deducted in full; a negative month total carries to the next month. Rows match when the sheet's agent column equals this code or name. Saving recalculates all months for this agent.</p>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button data-testid="agent-save-button" disabled={busy} className="bg-blue-500 hover:bg-blue-600 text-white">{busy ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function HouseCodes() {
  const [codes, setCodes] = useState("");
  useEffect(() => { api.get("/settings").then((r) => setCodes(r.data.house_codes.join(", "))); }, []);
  const save = async () => {
    const { data } = await api.put("/settings", { house_codes: codes.split(/[,\n]/) });
    setCodes(data.house_codes.join(", ")); toast.success("House accounts updated");
  };
  return (
    <Panel title="House account codes" className="mt-6">
      <div className="p-5 flex flex-col sm:flex-row gap-3">
        <Input data-testid="house-codes-input" value={codes} onChange={(e) => setCodes(e.target.value)} className="bg-[#0B0F17] font-mono" placeholder="08527-001, 03673-0001" />
        <Button data-testid="save-house-codes-button" variant="outline" onClick={save}>Save</Button>
      </div>
      <p className="px-5 pb-5 -mt-2 text-xs text-slate-500">Rows with these sales codes are reported as house residual — no split and no fee.</p>
    </Panel>
  );
}
